import axios from "axios"
import { useStoreActions } from "easy-peasy"
import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { beep } from "./beep.js"
import { LOGIN_URL, USER_SEND_RESET_PSWD_URL } from "./Constants.js"
import ErrorPopup from "./ErrorPopup.js"

const Login = () => {
	// Redux
	const inputRef = useRef(null)
	const [myStatus, setMyStatus] = useState("typing") // typing, submitting, or success
	const [error, setError] = useState("")
	const [answerLogin, setAnswerLogin] = useState("")
	const [answerPswd, setAnswerPswd] = useState("")

	// Easy-Peasy
	const setCounty = useStoreActions((actions) => actions.setCounty)
	const setDistrictList = useStoreActions((actions) => actions.setDistrictList)
	const setParcelDistrictList = useStoreActions(
		(actions) => actions.setParcelDistrictList,
	)
	const setBreedList = useStoreActions((actions) => actions.setBreedList)
	const setFeeList = useStoreActions((actions) => actions.setFeeList)
	const setTheme = useStoreActions((actions) => actions.setTheme)
	const setExpired = useStoreActions((actions) => actions.setExpired)
	const setHeaderCity = useStoreActions((actions) => actions.setHeaderCity)
	const setHeaderUser = useStoreActions((actions) => actions.setHeaderUser)
	const setLastname = useStoreActions((actions) => actions.setLastname)
	const setFirstname = useStoreActions((actions) => actions.setFirstname)
	const setLoggedIn = useStoreActions((actions) => actions.setLoggedIn)
	// const setEmail = useStoreActions((actions) => actions.setEmail)
	const setToken = useStoreActions((actions) => actions.setToken)
	const setAuthUser = useStoreActions((actions) => actions.setAuthUser)
	const setAuthFarm = useStoreActions((actions) => actions.setAuthFarm)
	const setAuthReports = useStoreActions((actions) => actions.setAuthReports)
	const setAuthAnimal = useStoreActions((actions) => actions.setAuthAnimal)
	const setAuthDashboard = useStoreActions(
		(actions) => actions.setAuthDashboard,
	)
	const setAuthDMV = useStoreActions((actions) => actions.setAuthDMV)
	const setAuthPersonalProp = useStoreActions((actions) => actions.setAuthPersonalProp)
	const setIsDeputy = useStoreActions((actions) => actions.setIsDeputy)
	const setSearchBy = useStoreActions((actions) => actions.setSearchBy)
	const setSearchPrompt = useStoreActions((actions) => actions.setSearchPrompt)
	const setEnvSuffix = useStoreActions((actions) => actions.setEnvSuffix)
	const navigate = useNavigate()

	useEffect(() => {
		const errorStatuses = [
			"Unauthorized",
			"Not Found",
			"Inactive",
			"Forbidden",
			"Unknown Error",
			"Network Error",
			"Exception",
		]
		if (errorStatuses.includes(myStatus)) beep()
	}, [myStatus])

	setSearchBy("") // Default search by is blank which returns all records when searching for customers or properties
	setSearchPrompt("") // Default search prompt is blank
	const handleLoginChange = (e) => {
		setAnswerLogin(e.target.value.toLowerCase())
		// setEmail(answerLogin)
		setError("")
	}

	const handlePswdChange = (e) => {
		setAnswerPswd(e.target.value)
		setError("")
	}

	async function handleSubmit(e) {
		e.preventDefault()
		setMyStatus("submitting")
		setSearchBy("")

		try {
			// Clienttime is the current datetime at the client location
			// converted to UTC in ISO format example: "2024-03-27T13:30:55.000Z"
			// It is passed to the api as a string
			// The purpose is to advise the server of the client's local time.
			// Database transactions are timestamped in UTC time.
			const request = {
				email: answerLogin,
				password: answerPswd,
			}
			// send to python fastapi
			const { data } = await axios.post(LOGIN_URL, request)

			// data returned from python fastapi
			// data = token followed by city_name and user_name
			// token = id, client_id, expires, theme, modules, roles

			setMyStatus("Success")
			setHeaderCity(data.client_name)
			setHeaderUser(data.firstname + " " + data.lastname)
			setLastname(data.lastname)
			setFirstname(data.firstname)
			setCounty(data.county)
			setDistrictList(data.district_list)
			setParcelDistrictList(data.parcel_district_list || [])
			setBreedList(data.breed_list)
			setLoggedIn(true)
			setToken(data.token)
			setTheme(data.token.theme)
			setExpired(false)
			setFeeList(data.fee_list)
			setIsDeputy(data.is_dog_deputy || false)
			setEnvSuffix(data.env_suffix || "LT")
			inputRef.current.focus()
			setAuthUser(false)
			setAuthFarm(false)
			setAuthReports(false)
			setAuthAnimal(false)
			setAuthDashboard(false)
			setAuthDMV(false)
			setAuthPersonalProp(false)

			let len = data.token.auth_modules.length
			for (let i = 0; i < len; i++) {
				switch (data.token.auth_modules[i]) {
					case 1:
						setAuthUser(true)
						break
					case 2:
						setAuthAnimal(true)
						break
					case 3:
						setAuthReports(true)
						break
					case 4:
						setAuthFarm(true)
						break
					case 5:
						setAuthDashboard(true)
						break
					case 6:
						setAuthDMV(true)
						break
					case 7:
						setAuthPersonalProp(true)
						break
					default:
						break
				}
			}
			navigate("/license/taxpayerfind")
		} catch (err) {
			if (err.message === "Request failed with status code 500") {
				setMyStatus("Unknown Error")
			} else if (err.message === "Request failed with status code 401") {
				setMyStatus("Unauthorized")
			} else if (err.message === "Request failed with status code 403") {
				setMyStatus("Forbidden")
			} else if (err.message === "Request failed with status code 404") {
				setMyStatus("Not Found")
			} else if (err.message === "Request failed with status code 409") {
				setMyStatus("Inactive")
			} else if (err.message === "Network Error") {
				setMyStatus("Network Error")
			} else {
				setMyStatus("Exception")
			}
			setHeaderCity("CR Cloud Technology")
			setHeaderUser("None")
		}
	}

	async function handleForgot(e) {
		e.preventDefault()
		try {
			const reset_data = {
				email: answerLogin,
			}
			await axios.post(USER_SEND_RESET_PSWD_URL, reset_data)
			setMyStatus("Resetting")
		} catch (err) {
			if (axios.isAxiosError(err)) {
				// console.log(err.response?.data?.detail)
				setMyStatus("No Network")
			} else {
				setMyStatus("typing")
				setError(err)
			}
		}
		setTimeout(() => {
			// remove the message line
			setError("")
			setMyStatus("typing")
		}, 3000)
	}

	return (
		<main className="Main">
			<fieldset className="panel-sm">
				<legend>User Login</legend>
				<form className="InputForm" name="InputForm" onSubmit={handleSubmit}>
					<table id="tbl_box" style={{ border: "none" }}>
						<tbody>
							<tr>
								<td style={{ textAlign: "right", border: "none" }}>
									<label htmlFor="userLogin">Email:</label>
								</td>
								<td style={{ textAlign: "left", border: "none" }}>
									<input
										id="userLogin"
										ref={inputRef}
										type="text"
										autoFocus
										autoComplete="off"
										placeholder="name@example.com"
										required
										onChange={(e) => handleLoginChange(e)}
										value={answerLogin}
									/>
								</td>
							</tr>
							<tr>
								<td style={{ textAlign: "right", border: "none" }}>
									<label htmlFor="userPswd">Password:</label>
								</td>
								<td style={{ textAlign: "left", border: "none" }}>
									<input
										id="userPswd"
										type="password"
										required
										onChange={(e) => handlePswdChange(e)}
										value={answerPswd}
									/>
								</td>
							</tr>
						</tbody>
					</table>
					<div className="InputItem" style={{ textAlign: "center" }}>
						<button
							type="submit"
							className="signinBtn"
							style={{ display: "inline-block" }}
							disabled={
								answerLogin.length === 0 ||
								answerPswd.length === 0 ||
								myStatus === "submitting"
							}
							onClick={(e) => handleSubmit(e)}
						>
							Login
						</button>
						<ErrorPopup
							message={
								myStatus === "Unauthorized" || myStatus === "Not Found"
									? "Invalid Login"
									: myStatus === "Inactive"
										? "User not activated."
										: myStatus === "Forbidden"
											? "City not authorized — Contact CR Cloud Technology"
											: myStatus === "Unknown Error"
												? "Unknown Error — Contact CR Cloud Technology"
												: myStatus === "Network Error"
													? "Network problem ... try again later."
													: myStatus === "Exception"
														? error
														: ""
							}
							onClose={() => setMyStatus("typing")}
						/>
						{myStatus === "Resetting" && (
							<p id="successMsg">Check your email for reset instructions</p>
						)}
					</div>
					<div className="InputItem" style={{ textAlign: "center" }}>
						<button
							type="button"
							className="forgotBtn"
							style={{ display: "inline-block" }}
							disabled={answerLogin.length === 0 || myStatus === "submitting"}
							onClick={(e) => handleForgot(e)}
						>
							Reset Password
						</button>
					</div>
				</form>
			</fieldset>
		</main>
	)
}

export default Login
